<template>
  <AccountCard :title="$t('pages.account.settings.sections.personal_details.title')">
    <hr class="my-6" />
    <div class="flex flex-row flex-wrap items-center gap-4">
      <VForm
        :validation-schema="Schema"
        :initial-values="Schema.getDefault()"
        v-slot="{ handleSubmit }"
        as="div"
        class="min-w-[15rem] flex-1"
      >
        <form @submit="handleSubmit($event, mutate)" class="max-w-lg">
          <CommonAlert v-if="error" type="error" :content="error.message" class="mb-6 mt-4" />
          <div class="mt-2 w-full">
            <div class="flex flex-row flex-wrap gap-x-2 md:gap-y-6 md:pt-4">
              <BaseVTextInput type="text" :label="$t('form.firstName')" name="first_name" class="flex-1 basis-full" />
              <BaseVTextInput :label="$t('form.lastName')" name="last_name" class="flex-1 basis-full" />

              <BaseVTextInput
                id="birthDay"
                name="date_of_birth"
                type="text"
                input-mask="##/##/####"
                placeholder="DD/MM/YYYY"
                :label="$t('form.dateOfBirth')"
                class="flex-1 basis-1/3"
              />

              <ClientOnly>
                <BaseVSelect
                  name="gender"
                  :options="genderOptions"
                  :placeholder="$t('form.gender')"
                  :label="$t('form.gender')"
                  class="h-10 flex-1 basis-1/3"
                />
              </ClientOnly>
            </div>

            <div class="mt-8 flex flex-row flex-wrap items-center gap-4">
              <BaseButton
                type="submit"
                class="w-auto max-w-full whitespace-nowrap text-[1rem]"
                :loading="pending"
                size="medium"
              >
                {{ $t("pages.account.buttons.save_changes") }}
              </BaseButton>

              <div class="flex-1 shrink-0 basis-1/3 text-primary">
                <div class="flex flex-row items-center gap-2" v-if="success">
                  <CheckCircleIcon class="h-[1.5em] w-[1.5em] shrink-0" />
                  <span>{{ $t("pages.account.texts.changes_saved") }}</span>
                </div>
              </div>
            </div>
          </div>
        </form>
      </VForm>

      <div class="flex-grow basis-60 rounded-md bg-[#D8D8D84D] p-4 text-sm md:flex-grow-0">
        {{ $t("pages.account.settings.sections.personal_details.note") }}
      </div>
    </div>
  </AccountCard>
</template>

<script setup lang="ts">
import { CheckCircleIcon } from "@heroicons/vue/solid";
import { AxiosError } from "axios";
import * as yup from "yup";
import type { User } from "~/entities/User";
import AccountCard from "../components/account-card.vue";

interface AccountSettingsPersonalDetailsProps {
  profileDetails: User.ProfileDetails;
}
const props = defineProps<AccountSettingsPersonalDetailsProps>();

const localProfile = reactive({ ...props.profileDetails }); // Create reactive local copy

const globalStore = useGlobalStore();

// @ts-ignore
const gender = globalStore.globalData.gender_options;
const genderOptions = [
  { value: "M", text: gender.M },
  { value: "F", text: gender.F },
  { value: "U", text: gender.U },
];

const Schema = yup.object({
  type: yup.string().optional().default("personal-detail"),
  first_name: yup.string().required(),
  last_name: yup.string().required(),
  date_of_birth: yup.string().required(),
  gender: yup.string().required().oneOf(Object.keys(gender)),
});
type Schema = yup.InferType<typeof Schema>;

Schema.getDefault = () => ({
  type: "personal-detail",
  first_name: localProfile.first_name ?? "",
  last_name: localProfile.last_name ?? "",
  date_of_birth: localProfile.date_of_birth,
  gender: localProfile.gender ?? "",
});

const credentialsCopy = reactive({});

const error = ref<Error>();
const { mutate, pending, success } = useMutation({
  mutationFn: async (credentials: Schema) => {
    Object.assign(credentialsCopy, credentials);
    //const payload = new URLSearchParams(credentials as any);
    return useAxios()
      .put("/profile/update", credentials, {
        // headers: {
        //   "Content-Type": "application/x-www-form-urlencoded",
        // },
      })
      .then((res) => res.data);
  },
  onError(err) {
    error.value = err;
    if (err instanceof AxiosError) error.value = new Error(err.response?.data.message);
  },
  onSuccess() {
    error.value = undefined;
    Object.assign(localProfile, credentialsCopy);
    delete localProfile.type;

    const profileStore = useProfileStore();
    profileStore.setProfile(localProfile);
  },
});
</script>
